import {
  useLocation,
  useNavigate as useRouterNavigate,
  useParams,
  type NavigateOptions,
} from "@solidjs/router";
import { createContext, useContext } from "solid-js";
import { tryUseServer } from "@/context/server";
import { usesDashboardBase } from "@/utils/app-mode";
import { injectMachine, parseMachineFromPath } from "@/utils/machine-route";

export function appPath(
  path: string,
  dashboardScoped:
    | boolean
    | { dashboardScoped?: boolean; machine?: string } = usesDashboardBase(),
) {
  const opts =
    typeof dashboardScoped === "boolean"
      ? { dashboardScoped, machine: undefined as string | undefined }
      : {
          dashboardScoped:
            dashboardScoped.dashboardScoped ?? usesDashboardBase(),
          machine: dashboardScoped.machine,
        };

  let next = path;
  if (opts.machine) next = injectMachine(next, opts.machine);

  if (
    !opts.dashboardScoped ||
    !next.startsWith("/") ||
    next === "/dashboard" ||
    next.startsWith("/dashboard/")
  ) {
    return next;
  }
  return next === "/" ? "/dashboard" : `/dashboard${next}`;
}

export function isWorkspaceSessionPath(pathname: string, directory?: string) {
  if (!directory) return false;
  const path = pathname.replace(/\/+$/, "");
  const escaped = directory.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `^(?:/dashboard)?(?:/m/[^/]+)?/${escaped}(?:/session(?:/[^/]+)?)?$`,
  );
  return pattern.test(path);
}

export function isWorkspaceOraclePath(pathname: string, directory?: string) {
  if (!directory) return false;
  const path = pathname.replace(/\/+$/, "");
  const escaped = directory.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^(?:/dashboard)?(?:/m/[^/]+)?/${escaped}/oracle$`).test(
    path,
  );
}

export function isWorkspaceChatPath(pathname: string, directory?: string) {
  return (
    isWorkspaceSessionPath(pathname, directory) ||
    isWorkspaceOraclePath(pathname, directory)
  );
}

export function isAnyWorkspaceChatPath(pathname: string) {
  const path = pathname.replace(/\/+$/, "");
  return /^(?:\/dashboard)?(?:\/m\/[^/]+)?\/[^/]+\/(?:session(?:\/[^/]+)?|oracle)$/.test(
    path,
  );
}

export function mobileChatNavigationOptions(input: {
  mobile: boolean;
  currentPath: string;
  targetPath: string;
}): Partial<NavigateOptions> | undefined {
  if (!input.mobile) return;
  if (!isAnyWorkspaceChatPath(input.currentPath)) return;
  if (!isAnyWorkspaceChatPath(input.targetPath)) return;
  return { replace: true };
}

/** A primary-button click without modifiers, which a link may handle in-app. */
export function isPlainLinkClick(event: MouseEvent) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** Resolve the active machine name from route params or pathname. */
export function routeMachineName(
  params?: { machine?: string },
  pathname?: string,
) {
  // Solid Router already decodes params. Do not decode it a second time.
  if (params?.machine) return params.machine;
  if (pathname) return parseMachineFromPath(pathname);
  return undefined;
}

/**
 * The machine a relative app path opens on: the machine scope's own machine,
 * otherwise the route's machine, otherwise the last machine shown.
 */
function pathMachine(
  server: ReturnType<typeof tryUseServer>,
  params: { machine?: string },
  pathname: string,
) {
  if (server?.bound && server.name) return server.name;
  return routeMachineName(params, pathname) || server?.name || undefined;
}

/**
 * A part of the page that shows destinations in place, such as a Grid window.
 * Navigation from inside it reaches the scope first.
 */
export type NavigationScope = {
  /** Show `path` in the scope; false when the scope cannot, so the page navigates. */
  open: (path: string) => boolean;
};

export const NavigationScopeContext = createContext<NavigationScope>();

export function useNavigate() {
  const navigate = useRouterNavigate();
  const location = useLocation();
  const params = useParams<{ machine?: string }>();
  const server = tryUseServer();
  const scope = useContext(NavigationScopeContext);

  return (to: string | number, options?: Partial<NavigateOptions>) => {
    if (typeof to === "number") return navigate(to);
    if (scope?.open(to)) return;
    const path = appPath(to, { machine: pathMachine(server, params, location.pathname) });
    // Reopening the current URL with new state (search revealing the setting
    // already shown) must not stack a duplicate history entry.
    const current = path === `${location.pathname}${location.search}${location.hash}`;
    return navigate(path, current ? { ...options, replace: true } : options);
  };
}

/** Build an href the same way useNavigate rewrites paths (for <A href>). */
export function useAppHref() {
  const location = useLocation();
  const params = useParams<{ machine?: string }>();
  const server = tryUseServer();
  return (path: string) => appPath(path, { machine: pathMachine(server, params, location.pathname) });
}
